import type { IObservableValue } from 'mobx';
import { action, computed, makeObservable, observable, runInAction, when } from 'mobx';
import type { IPromiseBasedObservable } from 'mobx-utils';
import { fromPromise } from 'mobx-utils';

import { concatPath, moment } from '@feathr/hooks';
import type { IBaseAttributes, IRachisMessage, TConstraints, TRachisEmpty } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';
import { BulkCollection } from '@feathr/rachis';

import type { Flavors } from './breadcrumbs';
import type { CampaignClass, ICampaignAttributes, IPinpointTrigger } from './campaigns';
import type { TAdChannelKey } from './campaigns/types';
import type { IGoal } from './goals';
import type { IReportAttributes } from './model/report';
import { ReportModel } from './model/report';
import type { DailyStats } from './stats';
import type { ITargeting } from './targetings';

export type TComparison =
  | 'eq'
  | 'eq_date'
  | 'eq_date_r'
  | 'ne'
  | 'ne_date'
  | 'ne_date_r'
  | 'gt'
  | 'gt_date'
  | 'gt_date_r'
  | 'gte_date'
  | 'gte_date_r'
  | 'gt_r'
  | 'gte'
  | 'gte_r'
  | 'lt'
  | 'lt_date'
  | 'lt_date_r'
  | 'lte_date'
  | 'lte_date_r'
  | 'lt_r'
  | 'lte'
  | 'lte_r'
  | 'in'
  | 'nin'
  | 'exists'
  | 'nexists'
  | 'contains'
  | 'ncontains'
  | 'starts_with'
  | 'regexp'
  | 'wildcard'
  | 'contains_substring';

export type TPredicateKind = 'attribute' | 'activity' | 'update' | 'mixed';
export type TPredicateMode = 'match_all' | 'match_any';
export type TLookbackMode = 'relative' | 'absolute' | 'unbounded' | 'before';

export interface ISegmentUsage extends Record<string, unknown> {
  active_campaigns: ICampaignAttributes[];
  targetings: ITargeting[];
  goals: IGoal[];
}

export interface IPredicate {
  attr_against?: string;
  comparison?: TComparison;
  attr_type?: 'date' | 'string' | 'integer' | 'float' | 'list' | 'boolean';
  value?: string | boolean | number | string[];
  unit?: 'days';
  kind?: TPredicateKind;
  filter_type?: 'all';
  group?: IPredicate[];
  group_mode?: TPredicateMode;
  excludes?: boolean;
  /** @deprecated */
  _kind?: TPredicateKind;
}

export interface IUniquePredicate extends IPredicate {
  key: string;
}

export interface IPredicateGroup {
  kind?: TPredicateKind;
  mode: TPredicateMode;
  group: IPredicate[];
  /** @deprecated */
  _kind?: TPredicateKind;
}

interface ISegmentStats {
  num_cookies_total: number;
  num_crumbs_total: number;
  num_emails_primary: number;
  /** Includes additional email addresses on a person */
  num_emails_total: number;
  num_users_active: number;
  num_users_new: number;
  num_users_total: number;
  num_users_reachable: number;
  num_users_reachable_meta: number;
}

interface IFacebookCustomAudience {
  /** Lower estimate of audience size in Facebook */
  lower_bound: number;
  /** Higher estimate of audience size in Facebook */
  upper_bound: number;
}

export interface ISegment extends IReportAttributes {
  account: string;
  date_active_end?: string;
  date_active_start?: string;
  fb_custom_audience?: IFacebookCustomAudience;
  is_conversion_segment: boolean;
  is_custom: boolean;
  is_intelligent?: boolean;
  kind?: 'person_segment';
  lookback_mode: TLookbackMode;
  lookback_units?: 'days' | 'hours' | 'minutes';
  lookback_value: string | number;
  mode: TPredicateMode;
  name?: string;
  parent_segment?: string;
  predicates: IPredicate[];
  read_only: boolean;
  // TODO: Use common interface that campaign uses for stats
  stats_cache?: Partial<ISegmentStats>;
  tag_ids?: string[];
  type: 'PersonSegment';
  user_can_clone: boolean;
  user_can_edit: boolean;
}

export interface ISegmentFetchFieldCardinalityResponse extends Record<string, unknown> {
  cardinality: number;
}

export interface ISegmentFetchCountResponse extends Record<string, unknown> {
  count: number;
}

type TGroupCloneParams = Partial<
  Pick<
    ISegment,
    'name' | 'predicates' | 'lookback_mode' | 'mode' | 'lookback_value' | 'lookback_units'
  >
>;

interface IPersonSegmentExport extends IBaseAttributes {
  created_by: string;
  date_created: string;
  export_field_headers: string[];
  export_fields: string[];
  export_url: string;
  internal: false;
  name: string;
  person_segment: string;
  rows: number;
  state: string;
}

interface ISegmentFetchExportResponse extends Record<string, unknown> {
  exports: IPersonSegmentExport[];
}

export interface IStatsPayload {
  predicates: IPredicate[];
  mode: TPredicateMode;
  lookback_mode: TLookbackMode;
  lookback_value: string | number;
  parent_segment?: string;
}

export interface ILimitedParams {
  campaign_id: string;
  end?: string;
  flavor: keyof typeof Flavors;
  name: string;
  start?: string;
}

type TURLVariant =
  | 'limitedAuthorize'
  | 'count'
  | 'crumbs'
  | 'limited'
  | 'reachable'
  | 'uniqueEmails'
  | 'targetableUniqueEmails'
  | 'audienceBreakdown'
  | 'forecast';

interface IUniqueEmailsResponse extends IBaseAttributes {
  unique_emails: number;
}

export interface IAudienceBreakdown extends IBaseAttributes {
  num_devices_reachable: {
    avg_per_person: number;
    estimated_new: number;
    total_targetable: number;
  };

  num_persons_reachable: {
    // Cookies and devices
    device: number;
    email: number;
    // Show on wizard & add to wizard's total
    lookalike_targeted: number;
    // Show on stats page & add to stats' total
    lookalike_actual: number;
    total: number;
    // Meta people targeted (wizard)
    meta_targeted?: number;
    // Meta lookalike targeted (wizard)
    meta_lookalike_targeted?: number;
    // Meta people reached (report)
    meta_actual?: number;
    // Meta lookalike reached (report)
    meta_lookalike_actual?: number;
  };
}

interface IAudienceBreakdownPayload {
  included_segment_ids?: string[];
  excluded_segment_ids?: string[];
  lookalike_targeting_id?: string;
  campaign_type?: CampaignClass;
  mode?: TPredicateMode;
  meta_campaign_id?: string;
}

export interface IForecast extends IBaseAttributes {
  max: number;
  min: number;
}

export interface IChannelForecastEntry {
  min: number;
  max: number;
}

export interface IChannelForecast {
  [key: string]: IChannelForecastEntry | undefined;
  tradedesk?: IChannelForecastEntry;
  meta?: IChannelForecastEntry;
}

export interface IForecastPayload {
  included_geo_filter_ids?: string[];
  excluded_geo_filter_ids?: string[];
  datagroup_data: Array<{
    interest_ids?: string[];
    age_category_ids?: string[];
  }>;
  channels?: TAdChannelKey[];
}

interface IUniqueEmailsPayload extends IAudienceBreakdownPayload {
  email_list_urls?: string[];
}

function getStatsPayload(segment: Segment): IStatsPayload {
  const parentSegment = segment.get('parent_segment');

  const payload = {
    predicates: segment.get('predicates'),
    mode: segment.get('mode'),
    lookback_mode: segment.get('lookback_mode'),
    lookback_value: segment.get('lookback_value'),
  };

  if (parentSegment) {
    payload['parent_segment'] = parentSegment;
  }

  return payload;
}

// Transform the triggers to match the Segment predicates. Update trigger kinds cannot be used for stats.
export function transformTriggersToPredicates(triggers: IPinpointTrigger[]): IPredicate[] {
  return triggers.map((trigger) => {
    if (trigger.kind === 'update') {
      return { ...trigger, kind: 'attribute' };
    }
    return trigger;
  });
}

export class Segment extends ReportModel<ISegment> {
  public readonly className = 'Segment';

  public reportKey = 's' as const;

  public oldCount = 0;

  public oldReachable = 0;

  public oldCrumbs = 0;

  public oldUniqueEmails = 0;

  @observable public statsComplete = 0.0;

  @observable countPromiseState = 'pending';

  constructor(attributes: Partial<ISegment> = {}) {
    super(attributes);

    makeObservable(this);
  }

  @computed
  get countPromise(): IPromiseBasedObservable<number> {
    return fromPromise(this.fetchCount());
  }

  @computed
  get count(): number {
    return this.countPromise.case({
      pending: (): number => {
        runInAction(() => {
          this.countPromiseState = 'pending';
        });
        return this.oldCount;
      },
      rejected: (): number => {
        runInAction(() => {
          this.countPromiseState = 'rejected';
        });

        return (this.oldCount = 0);
      },
      fulfilled: (value: number): number => {
        runInAction(() => {
          this.countPromiseState = 'fulfilled';
        });

        return (this.oldCount = value);
      },
    });
  }

  @computed
  public get name(): string {
    return (
      this.get('name', '').trim() ||
      (this.get('is_conversion_segment') ? 'Unnamed Conversion Pixel' : 'Unnamed Group')
    );
  }

  @computed
  public get permissions(): {
    canClone: boolean;
    canEdit: boolean;
    isReadOnly: boolean;
  } {
    return {
      canClone: this.get('user_can_clone'),
      canEdit: this.get('user_can_edit'),
      isReadOnly: this.isReadOnly,
    };
  }

  @computed
  get reachablePromise(): IPromiseBasedObservable<number> {
    return fromPromise(this.fetchReachable());
  }

  @computed
  get reachable(): number {
    return this.reachablePromise.case({
      pending: () => this.oldReachable,
      rejected: () => (this.oldReachable = 0),
      fulfilled: (value) => (this.oldReachable = value),
    });
  }

  @computed
  get uniqueEmailsPromise(): IPromiseBasedObservable<number> {
    return fromPromise(this.fetchUniqueEmails());
  }

  @computed
  get emails(): number {
    return this.uniqueEmailsPromise.case({
      pending: () => this.oldUniqueEmails,
      rejected: () => (this.oldUniqueEmails = 0),
      fulfilled: (value) => (this.oldUniqueEmails = value),
    });
  }

  @computed
  get crumbsPromise(): IPromiseBasedObservable<number> {
    return fromPromise(this.fetchCrumbs());
  }

  @computed
  get crumbs(): number {
    return this.crumbsPromise.case({
      pending: () => this.oldCrumbs,
      rejected: () => (this.oldCrumbs = 0),
      fulfilled: (value) => (this.oldCrumbs = value),
    });
  }

  public get constraints(): TConstraints<ISegment> {
    return {
      name: {
        presence: {
          allowEmpty: false,
        },
      },
    };
  }

  public override getDefaults(): Partial<ISegment> {
    return {
      _cls: 'PersonSegment',
      stats: {
        num_users_active: 0,
        num_users_new: 0,
        num_users_total: 0,
      },
    };
  }

  @action.bound
  public isActive(): boolean {
    const now = moment.utc();
    const activeStart = moment.utc(this.get('date_active_start'));
    if (this.get('date_active_end')) {
      const activeEnd = moment.utc(this.get('date_active_end'));
      return now.isBefore(activeEnd) && now.isAfter(activeStart);
    }
    return now.isAfter(activeStart);
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/data/segments/${this.id}`, pathSuffix);
  }

  public async fetchLiveStat<T extends Record<string, unknown>>(stat: TURLVariant): Promise<T> {
    this.assertCollection(this.collection);

    const url = this.collection.url(stat);
    const headers = this.collection.getHeaders();
    const payload = getStatsPayload(this);
    const response = await wretch<T>(url, {
      headers,
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async fetchCount(): Promise<number> {
    try {
      const { count } = await this.fetchLiveStat<ISegmentFetchCountResponse>('count');
      return count;
    } catch (error) {
      return 0;
    }
  }

  public async fetchReachable(): Promise<number> {
    try {
      const { cardinality } =
        await this.fetchLiveStat<ISegmentFetchFieldCardinalityResponse>('reachable');
      return cardinality;
    } catch (error) {
      return 0;
    }
  }

  public async fetchUniqueEmails(): Promise<number> {
    try {
      const { cardinality } =
        await this.fetchLiveStat<ISegmentFetchFieldCardinalityResponse>('uniqueEmails');
      return cardinality;
    } catch (error) {
      return 0;
    }
  }

  public async fetchCrumbs(): Promise<number> {
    try {
      const { count } = await this.fetchLiveStat<ISegmentFetchCountResponse>('crumbs');
      return count;
    } catch (error) {
      return 0;
    }
  }

  public getDateRange(start: string, end: string): number {
    const startMoment = moment.utc(start, moment.ISO_8601);
    const endMoment = moment.utc(end, moment.ISO_8601);

    if (endMoment.isBefore(startMoment)) {
      return 0;
    }

    // Exact same timestamp
    if (startMoment.isSame(endMoment)) {
      return 0;
    }

    // Same day but different timestamp
    if (startMoment.isSame(endMoment, 'day')) {
      return 1;
    }

    return endMoment.diff(startMoment, 'days');
  }

  @action
  public fetchStats(stats: DailyStats, start: string, end: string): IObservableValue<number> {
    this.assertCollection(this.collection);

    const url = `${BLACKBOX_URL}segments/${this.id}/generate?start=${start}&end=${end}`;
    const headers = this.collection.getHeaders();
    const statsComplete = observable.box(0);
    const expectedResults = this.getDateRange(start, end);
    // TODO: We should async/await the response from wretch, and add error handling.
    wretch<IRachisMessage>(url, { headers, method: 'GET' });
    const intervalHandle = window.setInterval(async () => {
      const segmentDailyStats = stats.list(
        {
          filters: {
            metadata__date__gte: start,
            metadata__date__lte: end,
            metadata__obj_id: this.id,
          },
          model: 'segment',
          ordering: ['metadata.date'],
          pagination: { page_size: expectedResults },
        },
        { reset: true },
      );
      await when(() => !segmentDailyStats.isPending);
      const currentResults = segmentDailyStats.models.length;
      const complete = currentResults / expectedResults;
      if (complete >= 1.0) {
        window.clearInterval(intervalHandle);
      }
      runInAction(() => {
        statsComplete.set(complete);
      });
    }, 3000);
    return statsComplete;
  }

  public async getUsage(): Promise<ISegmentUsage> {
    this.assertCollection(this.collection);

    const headers = this.collection.getHeaders();
    const url = `${BLACKBOX_URL}segments/${this.id}/usage`;
    const response = await wretch<ISegmentUsage>(url, {
      headers,
      method: 'GET',
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async isUnique(isCustom?: boolean): Promise<boolean> {
    this.assertCollection(this.collection);

    const count = await this.collection.count({
      filters: {
        is_custom: isCustom ?? this.get('is_custom'),
        predicates: this.get('predicates'),
      },
    });

    return count === 0;
  }

  public async export(
    exportFields: string[],
    exportFieldHeaders: string[],
    email: string,
    exportMode: 'persons' | 'breadcrumbs',
  ): Promise<ISegmentFetchExportResponse> {
    this.assertCollection(this.collection);

    const url = `${BLACKBOX_URL}segments/${this.id}/export`;
    const headers = this.collection.getHeaders();

    const response = await wretch<ISegmentFetchExportResponse>(url, {
      headers,
      method: 'POST',
      body: JSON.stringify({
        export_field_headers: exportFieldHeaders,
        export_fields: exportFields,
        email,
        export_mode: exportMode,
      }),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }
}

export class Segments extends BulkCollection<Segment> {
  public fullSegment: Segment | undefined = undefined;

  // TODO: Rename to something more descriptive
  public async authorize(campaignId: string): Promise<boolean> {
    const headers = this.getHeaders();
    const response = await wretch<TRachisEmpty>(this.url('limitedAuthorize'), {
      headers,
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
    if (isWretchError(response)) {
      return false;
    }
    return true;
  }

  public async limited(payload: ILimitedParams): Promise<Segment> {
    const headers = this.getHeaders();
    const response = await wretch<TRachisEmpty>(this.url('limited'), {
      headers,
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (isWretchError(response)) {
      throw response.error;
    }
    return this.processJSONResponse(response);
  }
  public async getUniqueEmailsFromTargetables(
    payload: IUniqueEmailsPayload,
  ): Promise<IUniqueEmailsResponse> {
    const response = await wretch<IUniqueEmailsResponse>(this.url('targetableUniqueEmails'), {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async getAudienceBreakdown(
    payload: IAudienceBreakdownPayload,
  ): Promise<IAudienceBreakdown> {
    const response = await wretch<IAudienceBreakdown>(this.url('audienceBreakdown'), {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getForecast(payload: IForecastPayload, signal?: AbortSignal): Promise<IForecast> {
    const response = await wretch<IForecast>(this.url('forecast'), {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  /**
   * Fetches per-channel reach forecast estimates from the forecast endpoint.
   * Unlike `getForecast`, this method requires a `channels` array and returns
   * per-channel `{ min, max }` entries keyed by channel name.
   * @param payload - Forecast payload including required `channels` array.
   * @param signal - Optional abort signal to cancel the request.
   * @returns Per-channel forecast entries (e.g., `{ tradedesk: { min, max }, meta: { min, max } }`).
   */
  public async getChannelForecast(
    payload: IForecastPayload & { channels: TAdChannelKey[] },
    signal?: AbortSignal,
  ): Promise<IChannelForecast> {
    const response = await wretch<IChannelForecast>(this.url('forecast'), {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async fetchCountWithPayload(
    payload: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<number> {
    const response = await wretch<ISegmentFetchCountResponse>(this.url('count'), {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data.count;
  }

  public getFullSegment(): Segment {
    if (!this.fullSegment) {
      this.fullSegment = this.create({
        name: 'Full Audience Group',
        lookback_mode: 'unbounded',
        lookback_value: 0,
        mode: 'match_any',
        predicates: [],
      });
    }
    return this.fullSegment;
  }

  public getModel(attributes: Partial<ISegment> = {}): Segment {
    return new Segment(attributes);
  }

  public getClassName(): string {
    return 'person_segments';
  }

  public override url(variant?: TURLVariant): string {
    const paths: Record<TURLVariant, string> = {
      count: 'persons/count',
      crumbs: 'breadcrumbs/count/',
      limited: 'segments/limited',
      limitedAuthorize: 'segments/limited/authorize',
      reachable: 'segments/field_cardinality/breadcrumbs.ttd_id',
      uniqueEmails: 'segments/field_cardinality/email',
      targetableUniqueEmails: 'segments/unique_emails',
      audienceBreakdown: 'segments/breakdown',
      forecast: 'segments/forecast',
    };
    return `${this.getHostname()}${variant ? paths[variant] : 'segments/'}`;
  }

  public override async clone(parentSegment: string, data: TGroupCloneParams): Promise<Segment> {
    return super.clone(parentSegment, data);
  }

  public async getOrCreateGoalSegment(
    predicates: IPredicate[],
    segmentName: string,
  ): Promise<IGetOrCreateGoalSegmentResult> {
    const existingSegments = this.list({
      filters: {
        is_archived__ne: true,
        is_custom: false,
        mode: 'match_all',
        lookback_mode: 'unbounded',
        lookback_value: 0,
        __raw__: {
          predicates: {
            $elemMatch: predicates[0],
          },
        },
      },
      pagination: {
        page_size: 1,
      },
    });

    await when(() => !existingSegments.isPending);

    if (existingSegments.models.length > 0) {
      return { segment: existingSegments.models[0], isNew: false };
    }

    const newSegment = this.create({
      is_custom: false,
      predicates: predicates,
      lookback_mode: 'unbounded',
      lookback_value: 0,
      mode: 'match_all',
      name: segmentName,
    });

    const addedSegment = await this.add(newSegment, { validate: false });

    return { segment: addedSegment, isNew: true };
  }
}

export interface IGetOrCreateGoalSegmentResult {
  segment: Segment;
  isNew: boolean;
}
